// src/contexts/AuthContext.tsx
// גרסה עם DEBUG מפורט

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  authService,
  User,
  LoginRequest,
} from "../services/backoffice/authService";

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // בדיקת אימות קיים בטעינת הדף
  useEffect(() => {
    const checkAuth = async () => {
      // console.log(
      //   "🔒 [DEBUG] AuthProvider - Checking existing authentication..."
      // );
      setIsLoading(true);

      try {
        const isValid = await authService.verifySession();
        // console.log("🔒 [DEBUG] AuthProvider - Session valid:", isValid);

        if (isValid) {
          setIsAuthenticated(true);
          // console.log("🔒 [DEBUG] AuthProvider - User is authenticated");
          // כאן נוכל לשלוף את פרטי המשתמש אם נרצה
        } else {
          setIsAuthenticated(false);
          setUser(null);
          // console.log("🔒 [DEBUG] AuthProvider - User is not authenticated");
        }
      } catch (error) {
        console.error("🔒 [DEBUG] AuthProvider - Auth check error:", error);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
        // console.log("🔒 [DEBUG] AuthProvider - Auth check completed");
      }
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginRequest): Promise<void> => {
    // console.log("🔒 [DEBUG] AuthProvider - Login attempt started");
    setIsLoading(true);

    try {
      const result = await authService.login(credentials);
      // console.log("🔒 [DEBUG] AuthProvider - Login result:", result);

      if (result.res === "OK") {
        // console.log("🔒 [DEBUG] AuthProvider - Setting user as authenticated");
        setIsAuthenticated(true);
        setUser(result.data.user_info);
        // console.log(
          // "🔒 [DEBUG] AuthProvider - User state updated:",
        //   result.data.user_info
        // );
        // console.log("🔒 [DEBUG] AuthProvider - isAuthenticated is now:", true);
      } else {
        console.error("🔒 [DEBUG] AuthProvider - Login failed:", result.msg);
        throw new Error(result.msg || "Login failed");
      }
    } catch (error) {
      console.error("🔒 [DEBUG] AuthProvider - Login error:", error);
      setIsAuthenticated(false);
      setUser(null);
      throw error;
    } finally {
      setIsLoading(false);
      // console.log("🔒 [DEBUG] AuthProvider - Login process completed");
    }
  };

  const logout = (): void => {
    // console.log("🔒 [DEBUG] AuthProvider - Logout called");
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    // console.log("🔒 [DEBUG] AuthProvider - User logged out");
  };

  // DEBUG: הדפסת מצב נוכחי
  // console.log("🔒 [DEBUG] AuthProvider - Current state:", {
  //   isAuthenticated,
  //   user: user?.name || "None",
  //   isLoading,
  // });

  const value = {
    isAuthenticated,
    user,
    login,
    logout,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
